import type { BrowserPartition } from './browser-contract';
import {
  browserEditorTabTitle,
  decideBrowserNavigation,
  describeBrowserTarget,
} from './browser-url';

/**
 * Titles the host publishes before the document's own title arrives.
 * They are not a page title and must not replace one that was committed.
 */
const UNCOMMITTED_BROWSER_TITLES = new Set(['New Tab', 'HiBro Browser']);

/** One saved editor tab. `title` is absent until the document names itself. */
export interface StoredBrowserEditorTab {
  url: string;
  title?: string;
}

/**
 * Committed editor-tab pages, keyed by dockview `slotId` (见 22).
 *
 * The workbench restores the tab shell. This catalog puts the page back and
 * names the tab before that page is attached. Only a persistent http(s)
 * document is stored — not the back stack, scroll position, or an ephemeral
 * partition.
 */
export interface BrowserEditorTabCatalog {
  read(slotId: string): string | undefined;
  /** Last document title saved with {@link read}. Absent when none was committed. */
  readTitle(slotId: string): string | undefined;
  /**
   * `url` must already be a page the built-in browser can load.
   * `title` replaces any saved title; omit it to clear the title.
   */
  remember(slotId: string, url: string, title?: string): void;
  forget(slotId: string): void;
}

export const BROWSER_EDITOR_TABS_VERSION = 1;

export interface BrowserEditorTabCatalogSnapshot {
  entries(): readonly { slotId: string; url: string; title?: string }[];
}

/** Label for a parked editor tab: the saved document title, else the hostname. */
export function parkedBrowserEditorTabTitle(
  url: string | undefined,
  title: string | undefined,
): string {
  if (title) {
    return title;
  }
  return url ? browserEditorTabTitle(url) : 'New Tab';
}

/**
 * A title worth keeping. Empty and the host's own placeholders
 * ({@link UNCOMMITTED_BROWSER_TITLES}) are not the document's name.
 */
export function committedBrowserEditorTabTitle(
  title: string | undefined,
): string | undefined {
  if (!title || UNCOMMITTED_BROWSER_TITLES.has(title)) {
    return undefined;
  }
  return title;
}

/** In-memory catalog. File and localStorage stores persist a snapshot of one. */
export function createMemoryBrowserEditorTabCatalog(
  slots: Readonly<Record<string, string | StoredBrowserEditorTab>> = {},
): BrowserEditorTabCatalog & BrowserEditorTabCatalogSnapshot {
  const stored = new Map<string, StoredBrowserEditorTab>();
  for (const [slotId, entry] of Object.entries(slots)) {
    stored.set(slotId, normalizeStoredTab(entry));
  }
  return {
    read(slotId) {
      return stored.get(slotId)?.url;
    },
    readTitle(slotId) {
      return stored.get(slotId)?.title;
    },
    remember(slotId, url, title) {
      const next: StoredBrowserEditorTab = {
        url: requireRestorableBrowserEditorTabUrl(url),
      };
      if (title !== undefined && title.length > 0) {
        next.title = title;
      }
      stored.set(slotId, next);
    },
    forget(slotId) {
      stored.delete(slotId);
    },
    entries() {
      return [...stored].map(([slotId, entry]) => ({
        slotId,
        url: entry.url,
        ...(entry.title !== undefined ? { title: entry.title } : {}),
      }));
    },
  };
}

/**
 * Keep the catalog aligned with the live page. An empty URL is not a signal
 * to drop a saved page (a slot can report one before its document commits).
 * Leaving the persistent http(s) partition does drop it.
 */
export function syncBrowserEditorTab(
  catalog: BrowserEditorTabCatalog,
  slotId: string,
  partition: BrowserPartition,
  url: string,
  title?: string,
): void {
  if (partition !== 'persistent') {
    catalog.forget(slotId);
    return;
  }
  if (url.length === 0) {
    return;
  }
  const decision = decideBrowserNavigation(url, 'chrome');
  if (decision.action !== 'load') {
    catalog.forget(slotId);
    return;
  }
  const previousUrl = catalog.read(slotId);
  const previousTitle = catalog.readTitle(slotId);
  const incoming = committedBrowserEditorTabTitle(title);
  // A new address does not keep the previous document's name. A placeholder
  // published before `page-title-updated` does not erase a name already saved.
  const nextTitle =
    incoming ?? (previousUrl === decision.url ? previousTitle : undefined);
  if (previousUrl === decision.url && previousTitle === nextTitle) {
    return;
  }
  catalog.remember(slotId, decision.url, nextTitle);
}

/** Parse a catalog file or storage value. Throws when the payload is unusable. */
export function parseBrowserEditorTabSlots(
  raw: unknown,
): Record<string, StoredBrowserEditorTab> {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('Stored browser editor tabs are invalid');
  }
  const value = raw as { version?: unknown; slots?: unknown };
  if (value.version !== BROWSER_EDITOR_TABS_VERSION || !isRecord(value.slots)) {
    throw new Error('Stored browser editor tabs are invalid');
  }
  const slots: Record<string, StoredBrowserEditorTab> = {};
  for (const [slotId, entry] of Object.entries(value.slots)) {
    if (
      slotId.length === 0 ||
      !isRecord(entry) ||
      typeof entry.url !== 'string' ||
      (entry.title !== undefined &&
        (typeof entry.title !== 'string' || entry.title.length === 0))
    ) {
      throw new Error('Stored browser editor tabs are invalid');
    }
    slots[slotId] = normalizeStoredTab({
      url: entry.url,
      ...(typeof entry.title === 'string' ? { title: entry.title } : {}),
    });
  }
  return slots;
}

export function serializeBrowserEditorTabSlots(
  entries: readonly { slotId: string; url: string; title?: string }[],
): string {
  const slots: Record<string, StoredBrowserEditorTab> = {};
  for (const entry of entries) {
    slots[entry.slotId] = {
      url: entry.url,
      ...(entry.title !== undefined ? { title: entry.title } : {}),
    };
  }
  return JSON.stringify(
    { version: BROWSER_EDITOR_TABS_VERSION, slots },
    null,
    2,
  );
}

function normalizeStoredTab(
  entry: string | StoredBrowserEditorTab,
): StoredBrowserEditorTab {
  if (typeof entry === 'string') {
    return { url: requireRestorableBrowserEditorTabUrl(entry) };
  }
  return {
    url: requireRestorableBrowserEditorTabUrl(entry.url),
    ...(entry.title !== undefined && entry.title.length > 0
      ? { title: entry.title }
      : {}),
  };
}

function requireRestorableBrowserEditorTabUrl(url: string): string {
  const decision = decideBrowserNavigation(url, 'chrome');
  if (decision.action !== 'load') {
    throw new Error(
      `Browser editor tab URL cannot be restored: ${describeBrowserTarget(url)}`,
    );
  }
  return decision.url;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
